/**
 * Drives rendering of feed track layers onto the marker pipeline.
 * Keeps per-layer previous positions so each layer animates independently.
 */

import type { Track } from '@vtak/shared';
import type { MapInstance, ViewMode } from '../map-setup';
import { renderTrackLayer, setLayerVisibility } from './index';

/** Per-layer previous positions ([lon, lat]) for smooth animation. */
const previousPositionsByLayer = new Map<string, Map<string, [number, number]>>();

/** Render a feed track layer and apply its current visibility. */
export function renderLayer(
  map: MapInstance,
  mode: ViewMode,
  layerId: string,
  tracks: Track[],
  visible: boolean,
): void {
  const previous = getPreviousPositions(layerId);
  renderTrackLayer(map, mode, layerId, tracks, previous);
  rememberPositions(previous, tracks);
  setLayerVisibility(mode, layerId, visible);
}

/** Get (or create) the previous-positions map for a layer. */
function getPreviousPositions(layerId: string): Map<string, [number, number]> {
  let positions = previousPositionsByLayer.get(layerId);
  if (!positions) {
    positions = new Map();
    previousPositionsByLayer.set(layerId, positions);
  }
  return positions;
}

/** Record each track's current position as the next animation's start point. */
function rememberPositions(
  previous: Map<string, [number, number]>,
  tracks: Track[],
): void {
  for (const track of tracks) {
    previous.set(track.uid, [track.lon, track.lat]);
  }
}
